import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { CustomSelect } from "@/components/ui/CustomSelect";
import {
  type ClockState,
  type ShiftRow,
  breakEnd,
  breakStart,
  clockIn,
  clockOut,
  fetchShiftsDashboard,
} from "./api";
import {
  formatDuration,
  formatHMS,
  formatLocalDateTime,
  formatLocalTime,
  useElapsed,
  useNowTick,
} from "./time";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function MyShiftsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("es") ? "es-ES" : "en-US";
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const pageSize = parsePageSize(params.get("page_size"));

  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["shifts-my", page, pageSize],
    queryFn: () => fetchShiftsDashboard(page, pageSize),
    refetchOnWindowFocus: true,
  });

  const [actionError, setActionError] = useState<string | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["shifts-my"], exact: false });

  const mClockIn = useMutation({ mutationFn: clockIn, onSuccess: invalidate });
  const mClockOut = useMutation({ mutationFn: clockOut, onSuccess: invalidate });
  const mBreakStart = useMutation({ mutationFn: breakStart, onSuccess: invalidate });
  const mBreakEnd = useMutation({ mutationFn: breakEnd, onSuccess: invalidate });

  const busy =
    mClockIn.isPending ||
    mClockOut.isPending ||
    mBreakStart.isPending ||
    mBreakEnd.isPending;

  const runAction = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
    } catch {
      setActionError(t("shifts.action_failed"));
    }
  };

  const data = query.data;

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    next.set("page_size", String(pageSize));
    setParams(next, { replace: false });
  };
  const setPageSize = (size: number) => {
    const next = new URLSearchParams(params);
    next.set("page", "1");
    next.set("page_size", String(size));
    setParams(next, { replace: false });
  };

  return (
    <div className="my-shifts-root">
      <h1 className="page-title">{t("shifts.title")}</h1>
      <p className="page-subtitle">{t("shifts.subtitle")}</p>
      {data && (
        <p className="page-subtitle">
          {t("shifts.tz_in")} {data.tz_label}
        </p>
      )}

      {query.isLoading && !data && (
        <p className="page-subtitle">{t("common.loading")}</p>
      )}

      {query.isError && (
        <p className="flash flash-error">{t("common.error.network")}</p>
      )}

      {actionError && <p className="flash flash-error">{actionError}</p>}

      {data && (
        <>
          {data.today_schedule.label && (
            <div
              className="card"
              style={{
                marginBottom: "1rem",
                padding: "0.75rem 1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <span className="summary-card-meta" style={{ fontWeight: 600 }}>
                {t("shifts.today_schedule")}:
              </span>
              <span style={{ fontWeight: 500 }}>{data.today_schedule.label}</span>
              {data.today_schedule.is_split && (
                <span
                  className="status-pill status-requested"
                  style={{ fontSize: "0.75em" }}
                >
                  {t("shifts.split_shift")}
                </span>
              )}
            </div>
          )}

          <div className="my-shifts-hero">
            <ClockWidget
              state={data.clock_state}
              onSickLeave={data.on_sick_leave}
              startedAt={data.open_shift?.starts_at ?? null}
              breakStartedAt={data.open_break?.started_at ?? null}
              tzName={data.tz_name}
              locale={locale}
              busy={busy}
              onClockIn={() => runAction(() => mClockIn.mutateAsync())}
              onClockOut={() => runAction(() => mClockOut.mutateAsync())}
              onBreakStart={() => runAction(() => mBreakStart.mutateAsync())}
              onBreakEnd={() => runAction(() => mBreakEnd.mutateAsync())}
            />
            <div className="kpi-grid">
              <KpiCard
                label={t("shifts.kpi.week")}
                hours={data.week.hours}
                contract={data.week.contract}
                pending={data.week.pending}
              />
              <KpiCard
                label={t("shifts.kpi.month")}
                hours={data.month.hours}
                contract={data.month.contract}
                pending={data.month.pending}
              />
            </div>
          </div>

          <ShiftsTable
            shifts={data.shifts}
            tzName={data.tz_name}
            locale={locale}
            pageSize={pageSize}
            onChangePageSize={setPageSize}
          />

          {data.pagination.total_pages > 1 && (
            <Pagination
              page={data.pagination.page}
              totalPages={data.pagination.total_pages}
              totalCount={data.pagination.total_count}
              onChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
}

function parsePageSize(raw: string | null): number {
  const n = parseInt(raw || "25", 10);
  if (!PAGE_SIZE_OPTIONS.includes(n)) return 25;
  return n;
}

// ─────────────────────────────────────────────────────────────────
// Clock widget
// ─────────────────────────────────────────────────────────────────

interface ClockWidgetProps {
  state: ClockState;
  onSickLeave: boolean;
  startedAt: string | null;
  breakStartedAt: string | null;
  tzName: string;
  locale: string;
  busy: boolean;
  onClockIn: () => void;
  onClockOut: () => void;
  onBreakStart: () => void;
  onBreakEnd: () => void;
}

function ClockWidget({
  state,
  onSickLeave,
  startedAt,
  breakStartedAt,
  tzName,
  locale,
  busy,
  onClockIn,
  onClockOut,
  onBreakStart,
  onBreakEnd,
}: ClockWidgetProps) {
  const { t } = useTranslation();
  const now = useNowTick(1000);
  const elapsedShift = useElapsed(startedAt);
  const elapsedBreak = useElapsed(breakStartedAt);

  const nowLabel = (() => {
    try {
      return new Intl.DateTimeFormat(locale, {
        timeZone: tzName,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(new Date(now));
    } catch {
      return "--:--";
    }
  })();

  return (
    <div
      className="card clock-widget"
      data-clock-state={state}
      data-is-clocked-in={state !== "OUT" ? "true" : "false"}
    >
      <div className="clock-widget-header">
        <div>
          <div className="summary-card-title">{t("shifts.clock")}</div>
          <div className="summary-card-meta">{t("shifts.clock_hint")}</div>
        </div>
        {state === "BREAK" && (
          <span className="status-pill status-requested">{t("shifts.on_break")}</span>
        )}
        {state === "IN" && (
          <span className="status-pill status-approved">{t("shifts.running")}</span>
        )}
        {state === "OUT" && onSickLeave && (
          <span className="status-pill status-cancelled">{t("shifts.sick_leave_pill")}</span>
        )}
      </div>

      <div className="clock-widget-time">{nowLabel}</div>

      {state === "BREAK" && (
        <>
          <div className="clock-widget-elapsed">
            <span className="summary-card-meta">{t("shifts.elapsed_break")}</span>
            <span className="clock-widget-elapsed-value">{formatHMS(elapsedBreak)}</span>
          </div>
          <div className="clock-widget-elapsed" style={{ marginTop: "0.25rem" }}>
            <span className="summary-card-meta">{t("shifts.elapsed_shift")}</span>
            <span className="clock-widget-elapsed-value">{formatHMS(elapsedShift)}</span>
          </div>
          <div className="clock-widget-meta">
            {t("shifts.started_at")}:{" "}
            <span>{formatLocalTime(startedAt, tzName, locale)}</span>
          </div>
          <div className="clock-action">
            <button
              type="button"
              className="btn btn-clock is-on-break"
              disabled={busy}
              onClick={onBreakEnd}
            >
              {busy ? t("shifts.processing") : t("shifts.break_end")}
            </button>
          </div>
        </>
      )}

      {state === "IN" && (
        <>
          <div className="clock-widget-elapsed">
            <span className="summary-card-meta">{t("shifts.elapsed")}</span>
            <span className="clock-widget-elapsed-value">{formatHMS(elapsedShift)}</span>
          </div>
          <div className="clock-widget-meta">
            {t("shifts.started_at")}:{" "}
            <span>{formatLocalTime(startedAt, tzName, locale)}</span>
          </div>
          <div
            className="clock-action-group"
            style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
          >
            <div className="clock-action" style={{ flex: 1 }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ width: "100%" }}
                disabled={busy}
                onClick={onBreakStart}
              >
                {t("shifts.break_start")}
              </button>
            </div>
            <div className="clock-action" style={{ flex: 1 }}>
              <button
                type="button"
                className="btn btn-clock is-clocked-in"
                style={{ width: "100%" }}
                disabled={busy}
                onClick={onClockOut}
              >
                {busy ? t("shifts.processing") : t("shifts.clock_out")}
              </button>
            </div>
          </div>
        </>
      )}

      {state === "OUT" && (
        <>
          {onSickLeave ? (
            <div
              className="flash flash-warning"
              style={{ margin: "0.75rem 0", display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <span>🏥</span>
              <span>{t("shifts.on_sick_leave_block")}</span>
            </div>
          ) : (
            <div className="clock-widget-meta">{t("shifts.tap_start")}</div>
          )}
          <div className="clock-action">
            <button
              type="button"
              className="btn btn-clock"
              disabled={busy || onSickLeave}
              onClick={onClockIn}
            >
              {busy ? t("shifts.processing") : t("shifts.clock_in")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// KPI card
// ─────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  hours,
  contract,
  pending,
}: {
  label: string;
  hours: number;
  contract: number;
  pending: number;
}) {
  const { t } = useTranslation();
  const hasContract = contract > 0;
  const progress = hasContract ? Math.min(100, (hours / contract) * 100) : 0;
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{hours} h</div>
      {hasContract ? (
        <>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="summary-card-meta">
            {t("shifts.kpi.contract")}: {contract} h · {t("shifts.kpi.pending")}: {pending} h
          </div>
          <div className="kpi-note">
            {pending > 0
              ? `${t("shifts.kpi.need")} ${pending} h`
              : t("shifts.kpi.goal")}
          </div>
        </>
      ) : (
        <div className="summary-card-meta">{t("shifts.kpi.no_contract")}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Shifts table
// ─────────────────────────────────────────────────────────────────

interface ShiftsTableProps {
  shifts: ShiftRow[];
  tzName: string;
  locale: string;
  pageSize: number;
  onChangePageSize: (n: number) => void;
}

function ShiftsTable({ shifts, tzName, locale, pageSize, onChangePageSize }: ShiftsTableProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "0.75rem",
          gap: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <div className="summary-card-title" style={{ margin: 0 }}>
          {t("shifts.recent")}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label className="form-label" htmlFor="page_size" style={{ margin: 0 }}>
            {t("shifts.rows_per_page")}
          </label>
          <CustomSelect
            value={String(pageSize)}
            options={PAGE_SIZE_OPTIONS.map((opt) => ({ value: String(opt), label: String(opt) }))}
            onChange={(v) => onChangePageSize(parseInt(v, 10))}
            allowEmpty={false}
          />
        </div>
      </div>

      {shifts.length === 0 ? (
        <p className="page-subtitle" style={{ margin: 0 }}>
          {t("shifts.empty")}
        </p>
      ) : (
        <div className="table-wrapper">
          <table className="table table-sticky">
            <thead>
              <tr>
                <th>{t("shifts.started_at")}</th>
                <th>{t("shifts.ended_at")}</th>
                <th>{t("shifts.duration")}</th>
                <th>{t("shifts.status")}</th>
                <th>{t("shifts.direction")}</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((s) => {
                const isExpanded = expanded === s.id;
                return (
                  <ShiftRowItem
                    key={s.id}
                    shift={s}
                    tzName={tzName}
                    locale={locale}
                    expanded={isExpanded}
                    onToggle={() => setExpanded(isExpanded ? null : s.id)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ShiftRowItem({
  shift,
  tzName,
  locale,
  expanded,
  onToggle,
}: {
  shift: ShiftRow;
  tzName: string;
  locale: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <tr
        className="shift-row"
        onClick={onToggle}
        style={{ cursor: "pointer" }}
      >
        <td>
          <span className="shift-dt">{formatLocalDateTime(shift.starts_at, tzName, locale)}</span>
        </td>
        <td>
          {shift.ends_at ? (
            <span className="shift-dt">{formatLocalDateTime(shift.ends_at, tzName, locale)}</span>
          ) : (
            <span className="summary-card-meta">{t("shifts.running_short")}…</span>
          )}
        </td>
        <td>
          <span className="summary-card-meta">
            {formatDuration(shift.starts_at, shift.ends_at)}
          </span>
        </td>
        <td>
          {shift.is_open ? (
            <span className="status-pill status-requested">{t("shifts.running_short")}</span>
          ) : (
            <span className="status-pill status-approved">{t("shifts.completed")}</span>
          )}
        </td>
        <td>
          {shift.direction && (
            <span className="direction-chip">{titleCase(String(shift.direction))}</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="shift-details-row is-open">
          <td colSpan={5}>
            <div className="shift-details">
              <div>
                <strong>{t("shifts.note")}:</strong>{" "}
                {shift.note || t("shifts.no_notes")}
              </div>
              <div>
                <strong>{t("shifts.exact_start")}:</strong>{" "}
                <span className="shift-dt-raw">{shift.starts_at ?? "—"}</span>
              </div>
              <div>
                <strong>{t("shifts.exact_end")}:</strong>{" "}
                <span className="shift-dt-raw">
                  {shift.ends_at ?? t("shifts.open_shift")}
                </span>
              </div>
              {shift.breaks.length > 0 && (
                <div style={{ marginTop: "0.5rem" }}>
                  <strong>{t("shifts.breaks")}:</strong>
                  <ul
                    style={{
                      margin: "0.25rem 0 0 1rem",
                      padding: 0,
                      listStyle: "disc",
                    }}
                  >
                    {shift.breaks.map((b) => {
                      const start = b.started_at
                        ? formatLocalTime(b.started_at, tzName, locale)
                        : "?";
                      const end = b.ended_at
                        ? formatLocalTime(b.ended_at, tzName, locale)
                        : t("shifts.ongoing");
                      const typeSuffix =
                        b.break_type && b.break_type !== "rest"
                          ? ` (${b.break_type})`
                          : "";
                      return (
                        <li className="summary-card-meta" key={b.id}>
                          {start} – {end}
                          {typeSuffix}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

// ─────────────────────────────────────────────────────────────────
// Pagination
// ─────────────────────────────────────────────────────────────────

function Pagination({
  page,
  totalPages,
  totalCount,
  onChange,
}: {
  page: number;
  totalPages: number;
  totalCount: number;
  onChange: (p: number) => void;
}) {
  const { t } = useTranslation();
  const startPage = Math.max(1, page - 2);
  const endPage = Math.min(totalPages, page + 2);
  const pages: number[] = [];
  for (let p = startPage; p <= endPage; p++) pages.push(p);

  const linkClass = (disabled: boolean) =>
    "btn btn-secondary btn-sm pagination-button" + (disabled ? " is-disabled" : "");

  return (
    <div className="pagination">
      <Link
        to="#"
        className={linkClass(page === 1)}
        onClick={(e) => {
          e.preventDefault();
          if (page !== 1) onChange(1);
        }}
      >
        « {t("shifts.first")}
      </Link>
      <Link
        to="#"
        className={linkClass(page === 1)}
        onClick={(e) => {
          e.preventDefault();
          if (page > 1) onChange(page - 1);
        }}
      >
        ‹ {t("shifts.prev")}
      </Link>

      <div className="pagination-pages">
        {pages.map((p) =>
          p === page ? (
            <span key={p} className="page-chip page-chip-active">
              {p}
            </span>
          ) : (
            <Link
              key={p}
              to="#"
              className="page-chip"
              onClick={(e) => {
                e.preventDefault();
                onChange(p);
              }}
            >
              {p}
            </Link>
          )
        )}
      </div>

      <Link
        to="#"
        className={linkClass(page === totalPages)}
        onClick={(e) => {
          e.preventDefault();
          if (page < totalPages) onChange(page + 1);
        }}
      >
        {t("shifts.next")} ›
      </Link>
      <Link
        to="#"
        className={linkClass(page === totalPages)}
        onClick={(e) => {
          e.preventDefault();
          if (page !== totalPages) onChange(totalPages);
        }}
      >
        {t("shifts.last")} »
      </Link>
      <span className="summary-card-meta" style={{ marginLeft: "0.35rem" }}>
        {t("shifts.page_summary", { page, total: totalPages, count: totalCount })}
      </span>
    </div>
  );
}
